import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, query, where, orderBy, onSnapshot, serverTimestamp, updateDoc, limit, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import OppModal, { getValueRangeLabel, formatActualValue } from '../components/forms/OppModal'; // Adjust the import based on your file structure
import { sanitizeString } from '../utils/sanitization';
import { formatDate, formatDateMMDDYYYY, formatDateYYYYMMDD, formatDateTime, parseLocalDate } from '../utils/dateHelpers';
import { useAuth } from '../components/auth/AuthContext';
import { collectionPath } from '../utils/firestoreCollections';
import { logEntityChanges } from '../utils/logger';
import { generateLogId } from '../utils/documentIdUtils';
import { usePermissions } from '../hooks/usePermissions';
import { monitorQuery } from '../utils/queryMonitoring';
import { uploadAttachment, deleteAttachment, subscribeToAttachments, checkAttachmentExists } from '../utils/attachmentsUtils';
import { fetchTasksByEntity } from '../utils/associationUtils';
import UnifiedNotes from '../components/common/UnifiedNotes';
import FollowUpCard from '../components/opportunities/FollowUpCard';
import QualificationCard from '../components/opportunities/QualificationCard';
import { FEATURE_PERMISSIONS, canModifyData } from '../utils/permissions';
import PaginationControls from '../components/common/PaginationControls';
import { useQuoting } from '../hooks/useQuoting';

const NOTES_PAGE_SIZE = 10;
const LOGS_PAGE_SIZE = 10;
const LOG_PAGE_SIZE = 25;

function isPastOrToday(dateStrOrObj) {
    if (!dateStrOrObj) return false;
    let date;
    if (typeof dateStrOrObj === 'string') {
        date = new Date(dateStrOrObj + 'T00:00:00');
    } else if (dateStrOrObj.seconds) {
        date = new Date(dateStrOrObj.seconds * 1000);
    } else {
        date = new Date(dateStrOrObj);
    }
    const today = new Date();
    today.setHours(0,0,0,0);
    date.setHours(0,0,0,0);
    return date <= today;
}

const OppDetailPage = ({ user, users = [], customers = [] }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth(); // <-- already present
    const { can, loading: permissionsLoading } = usePermissions(user);
    const quoting = useQuoting(user);
    const [opportunity, setOpportunity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [customerName, setcustomerName] = useState('');
    const [customerLocation, setCustomerLocation] = useState('');
    const [customerData, setCustomerData] = useState(null);
    const [salesRepName, setSalesRepName] = useState('');
    // Opportunity Notes state
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [notesSearch, setNotesSearch] = useState('');
    const [notesPage, setNotesPage] = useState(1);
    const [notesCount, setNotesCount] = useState(0);
    const [tasks, setTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(false);

    // Activity Log state
    const [activityLogs, setActivityLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [activitySearch, setActivitySearch] = useState('');
    const [logsPage, setLogsPage] = useState(1);

    // Tabs state
    const [activeTab, setActiveTab] = useState('notes');
    const [showEditModal, setShowEditModal] = useState(false);

    // New states for detailed logs
    const [logs, setLogs] = useState([]);
    const [logSearch, setLogSearch] = useState('');
    const [logPage, setLogPage] = useState(1);

    // Attachments state
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const fileInputRef = useRef(null);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        // Wait for permissions to load before checking access
        if (permissionsLoading) return;
        const fetchOpportunity = async () => {
            const docRef = doc(db, collectionPath('opportunities'), id);
            const docSnap = await monitorQuery('fetch-opportunity-detail', () => getDoc(docRef));
            if (docSnap.exists()) {
                const opp = { id: docSnap.id, ...docSnap.data() };
                
                // RBAC: Non-admin users can only view opportunities assigned to them
                if (!can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)) {
                    if (String(opp.uid) !== String(user?.uid) && String(opp.uid) !== String(user?.id)) {
                        console.warn('Access denied: Opportunity not assigned to current user');
                        navigate('/opportunities');
                        return;
                    }
                }
                setOpportunity(opp);

                // Customer Name - use denormalized field first (prevents permission-denied errors)
                // Fallback to customer lookup only if available
                let resolvedName = opp.customerName || '-';
                let resolvedLocation = '';
                
                // Try to find customer in passed-in customers list or fetch from Firestore
                if (opp.customerId) {
                    const customer = customers.find(c => 
                        String(c.id) === String(opp.customerId) ||
                        String(c.customerId) === String(opp.customerId)
                    );
                    
                    if (customer) {
                        if (!opp.customerName) {
                            resolvedName = customer.customerName || customer.name || '-';
                        }
                        // Build location string from customer
                        resolvedLocation = [
                            customer.customerCity,
                            customer.customerState,
                            customer.customerCountry
                        ].filter(Boolean).join(', ');
                        setCustomerData(customer);
                    } else {
                        // Fetch customer from Firestore if not in passed list
                        try {
                            const customerRef = doc(db, collectionPath('customers'), opp.customerId);
                            const customerSnap = await getDoc(customerRef);
                            if (customerSnap.exists()) {
                                const fetchedCustomer = customerSnap.data();
                                if (!opp.customerName) {
                                    resolvedName = fetchedCustomer.customerName || '-';
                                }
                                resolvedLocation = [
                                    fetchedCustomer.customerCity,
                                    fetchedCustomer.customerState,
                                    fetchedCustomer.customerCountry
                                ].filter(Boolean).join(', ');
                                setCustomerData(fetchedCustomer);
                            }
                        } catch (err) {
                            console.warn('Could not fetch customer for location:', err);
                        }
                    }
                }
                
                setcustomerName(resolvedName);
                setCustomerLocation(resolvedLocation);

                // Prefer passed users list; fetch only if missing
                if (opp.uid) {
                    const u = users.find(u => u.id === opp.uid);
                    if (u) {
                        setSalesRepName(
                            (u.firstName && u.lastName && `${u.firstName} ${u.lastName}`) ||
                            u.displayName || u.email || u.id || '-'
                        );
                    } else {
                        const userRef = doc(db, collectionPath('users'), opp.uid);
                        const userSnap = await getDoc(userRef);
                        if (userSnap.exists()) {
                            const u2 = userSnap.data();
                            setSalesRepName(
                                (u2.firstName && u2.lastName && `${u2.firstName} ${u2.lastName}`) ||
                                u2.displayName || u2.email || userSnap.id || '-'
                            );
                        } else {
                            setSalesRepName(opp.uid || '-');
                        }
                    }
                } else {
                    setSalesRepName('-');
                }
            }
            setLoading(false);
        };
        fetchOpportunity();
        // eslint-disable-next-line
    }, [id, customers, users, can, permissionsLoading]);

    // Fetch tasks associated with this opportunity
    // Uses utility that handles both associations.opportunityId and root-level opportunityId
    const refreshTasks = React.useCallback(() => {
        if (!id) return;
        setTasksLoading(true);
        fetchTasksByEntity('opportunity', id)
            .then(tasksData => {
                setTasks(tasksData);
                setTasksLoading(false);
            })
            .catch(error => {
                console.error('Error fetching opportunity tasks:', error);
                setTasksLoading(false);
            });
    }, [id]);

    useEffect(() => {
        refreshTasks();
    }, [refreshTasks]);

    // Notes are now handled by UnifiedNotes component
    // Legacy note loading and add functions removed - see UnifiedNotes

    // Activity Log logic - query only logs for this specific opportunity
    useEffect(() => {
        if (!id) return;
        setLogsLoading(true);
        // Query by entityId to get only logs for this specific opportunity
        const q = query(
            collection(db, collectionPath('logsOpp')),
            where('entityId', '==', id),
            orderBy('timestamp', 'desc'),
            limit(50) // Limit to 50 most recent logs
        );
        const unsub = onSnapshot(
            q,
            (snap) => {
                const filtered = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setActivityLogs(filtered);
                setLogsLoading(false);
            },
            (err) => {
                console.error('Activity log subscription error:', err);
                setActivityLogs([]);
                setLogsLoading(false);
            }
        );
        return unsub;
    }, [id]);

    // Subscribe to attachments
    useEffect(() => {
        if (!id) return;
        const unsub = subscribeToAttachments(id, 'opportunity', setAttachments);
        return () => unsub();
    }, [id]);

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setUploadError('');
        setUploading(true);

        try {
            const willOverwrite = await checkAttachmentExists({
                fileName: file.name,
                entityId: id,
                entityType: 'opportunity'
            });

            if (willOverwrite) {
                const confirm = window.confirm(
                    `A file named "${file.name}" already exists. Uploading will overwrite the existing file. Do you want to continue?`
                );
                if (!confirm) {
                    setUploading(false);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                }
            }

            await uploadAttachment({
                file,
                entityId: id,
                entityType: 'opportunity',
                currentUser
            });
            setToast({ type: 'success', message: 'File uploaded successfully.' });

            // Log the upload action to the opportunity activity log
            try {
                const attQ = query(
                    collection(db, collectionPath('attachments')),
                    where('entityId', '==', id),
                    where('entityType', '==', 'opportunity'),
                    where('name', '==', file.name),
                );
                const attSnap = await getDocs(attQ);
                const attDoc = !attSnap.empty ? attSnap.docs[0] : null;
                const attData = attDoc ? { id: attDoc.id, ...attDoc.data() } : null;

                const details = attData
                    ? `File "${file.name}" uploaded (attachment id: ${attData.id}).`
                    : `File "${file.name}" uploaded.`;

                const logId = generateLogId('opp', id, 'uploaded_attachment');
                await setDoc(doc(db, collectionPath('logsOpp'), logId), {
                    opportunityId: id,
                    entityType: 'Opportunity',
                    entityId: id,
                    userId: currentUser?.uid || null,
                    userEmail: currentUser?.email || null,
                    action: 'Uploaded Attachment',
                    details,
                    attachmentId: attData?.id || null,
                    attachmentUrl: attData?.url || null,
                    timestamp: serverTimestamp(),
                });
            } catch (logErr) {
                console.warn('Failed to log attachment upload to opportunity activity log:', logErr);
            }
        } catch (err) {
            setUploadError(err.message || 'Upload failed.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDeleteAttachment = async (attachment) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete "${attachment.name}"?`);
        if (!confirmDelete) return;

        try {
            await deleteAttachment(attachment);
            setToast({ type: 'success', message: 'Attachment deleted successfully.' });

            // Log the delete action
            try {
                const logId = generateLogId('opp', id, 'deleted_attachment');
                await setDoc(doc(db, collectionPath('logsOpp'), logId), {
                    opportunityId: id,
                    entityType: 'Opportunity',
                    entityId: id,
                    userId: currentUser?.uid || null,
                    userEmail: currentUser?.email || null,
                    action: 'Deleted Attachment',
                    details: `File "${attachment.name}" deleted.`,
                    attachmentId: attachment.id,
                    timestamp: serverTimestamp(),
                });
            } catch (logErr) {
                console.warn('Failed to log attachment deletion:', logErr);
            }
        } catch (err) {
            setToast({ type: 'error', message: err.message || 'Failed to delete attachment.' });
        }
    };

    // Filter and paginate notes (memoized)
    const filteredNotes = useMemo(() => {
        const q = (notesSearch || '').toLowerCase().trim();
        if (!q) return notes;
        return notes.filter(note => (note.note || '').toLowerCase().includes(q));
    }, [notes, notesSearch]);

    const paginatedNotes = useMemo(() => {
        return filteredNotes.slice((notesPage - 1) * NOTES_PAGE_SIZE, notesPage * NOTES_PAGE_SIZE);
    }, [filteredNotes, notesPage]);

    const notesTotalPages = useMemo(
        () => Math.ceil(filteredNotes.length / NOTES_PAGE_SIZE),
        [filteredNotes.length]
    );
    useEffect(() => { setNotesPage(1); }, [filteredNotes.length, notesSearch]);

    // Auto-hide toast after 5 seconds
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // Filter and paginate activity logs (memoized)
    const filteredLogs = useMemo(() => {
        const q = (logSearch || '').toLowerCase().trim();
        if (!q) return activityLogs;
        return activityLogs.filter(log =>
            (log.action || '').toLowerCase().includes(q) ||
            (log.details || '').toLowerCase().includes(q) ||
            (log.userEmail || '').toLowerCase().includes(q) ||
            (log.userName || '').toLowerCase().includes(q) ||
            (log.displayName || '').toLowerCase().includes(q) ||
            (log.timestamp && log.timestamp.toDate && log.timestamp.toDate().toLocaleString().toLowerCase().includes(q))
        );
    }, [activityLogs, logSearch]);

    const paginatedLogs = useMemo(() => {
        return filteredLogs.slice((logsPage - 1) * LOGS_PAGE_SIZE, logsPage * LOGS_PAGE_SIZE);
    }, [filteredLogs, logsPage]);

    const totalLogPages = Math.ceil(filteredLogs.length / LOG_PAGE_SIZE);

    const logsTotalPages = useMemo(
        () => Math.ceil(filteredLogs.length / LOGS_PAGE_SIZE),
        [filteredLogs.length]
    );
    useEffect(() => { setLogsPage(1); }, [filteredLogs.length, logSearch]);

    const handleEditOpportunity = async (updatedData) => {
        if (!id) return;
        try {
            const opportunityRef = doc(db, collectionPath('opportunities'), id);
            await updateDoc(opportunityRef, updatedData);

            // Log the edit action
            const logId = generateLogId('opp', id, 'edited');
            await setDoc(doc(db, collectionPath('logsOpp'), logId), {
                opportunityId: opportunity.id || id, // <-- Make sure this is set!
                action: 'Edited Opportunity',
                details: 'Changed stage to Qualified',
                userEmail: user.email,
                userId: user.uid,
                timestamp: serverTimestamp(),
            });
        } catch (err) {
            // Optionally handle error
        }
    };

    const STAGE_OPTIONS = [
      'New',
      'Qualified',
      'Not Qualified',
      'Proposal Sent',
      'Closed Won',
      'Closed Lost'
    ];

    const canEditStage = can(FEATURE_PERMISSIONS.OPP_EDIT) || opportunity?.uid === user?.uid;

    const handleStageChange = async (newStage) => {
      try {
        const oldStage = opportunity?.stage;
        await updateDoc(doc(db, collectionPath('opportunities'), id), { stage: newStage });
        setOpportunity(prev => ({ ...prev, stage: newStage }));
        
        // Log stage change
        await logEntityChanges({
          oldObj: { stage: oldStage },
          newObj: { stage: newStage },
          fields: ['stage'],
          entityType: 'Opportunity',
          entityId: id,
          user,
          action: 'Updated',
        });
        
        // Cloud function creates a new task on stage change — refresh after a short delay
        setTimeout(() => refreshTasks(), 3000);
      } catch (e) {
        // optional toast
      }
    };

    const handleLeadSourceChange = async (newLeadSource) => {
      if (!id || !opportunity) return;
      try {
        // Update the opportunity's lead source
        await updateDoc(doc(db, collectionPath('opportunities'), id), { leadSource: newLeadSource });

        // Log the lead source change
        await logEntityChanges({
          oldObj: opportunity,
          newObj: { ...opportunity, leadSource: newLeadSource },
          fields: ['leadSource'],
          entityType: 'Opportunity',
          entityId: opportunity.id || id,
          user,
          collectionName: collectionPath('logsOpp')
        });

        setOpportunity(prev => ({ ...prev, leadSource: newLeadSource }));
      } catch (err) {
        // Optionally handle error
      }
    };

    if (loading) return <div className="p-6">Loading...</div>;
    if (!opportunity) return <div className="p-6 text-red-600">Opportunity not found.</div>;

    // Prepare fields in desired order
    const {
        opportunityNumber,
        leadSource,
        stage,
        dueDate,
        closedDate,
        startDate,
        value
    } = opportunity;

    // Split into two columns: right column starts with Start Date
    // For lead/new customers, fall back to customer's imported contact fields
    const resolvedContactName = opportunity.contactName || customerData?.importedContactName || '-';
    const resolvedContactTitle = opportunity.contactTitle || customerData?.importedContactTitle || '';
    const resolvedContactEmail = opportunity.contactEmail || customerData?.importedContactEmail || '';
    const resolvedContactPhone = opportunity.contactPhone || customerData?.importedContactPhone || customerData?.customerPhone || '';

    const leftFields = [
        { label: 'Opportunity Number', value: opportunity.opportunityNumberLabel || id || '-' },
        { label: 'Customer Name', value: customerName || '-' },
        ...(customerLocation ? [{ label: 'Customer Location', value: customerLocation }] : []),
        { label: 'Contact Name', value: resolvedContactName },
        ...(resolvedContactTitle ? [{ label: 'Contact Title', value: resolvedContactTitle }] : []),
        { label: 'Contact Email', value: resolvedContactEmail ? (
            <a href={`mailto:${resolvedContactEmail}`} className="text-indigo-600 hover:underline">
                {resolvedContactEmail}
            </a>
        ) : '-' },
        { label: 'Contact Phone', value: resolvedContactPhone ? (
            <a href={`tel:${resolvedContactPhone}`} className="text-indigo-600 hover:underline">
                {resolvedContactPhone}
            </a>
        ) : '-' },
        { label: 'Lead Source', value: leadSource || '-' },
        { label: 'Estimated Value', value: value ? getValueRangeLabel(value) : '-' },
        ...(opportunity.actualValue ? [{ label: 'Actual Value', value: formatActualValue(opportunity.actualValue) }] : []),
        { label: 'Sales Rep', value: salesRepName || '-' }
    ];
    const rightFields = [
        { label: 'Start Date', value: startDate ? formatDate(parseLocalDate(startDate), currentUser?.dateFormat) : '-' },
        {
            label: 'Due Date',
            value: (
                <span className={
                    isPastOrToday(dueDate) && stage !== 'Closed Won' && stage !== 'Closed Lost'
                        ? 'font-bold text-red-700'
                        : ''
                }>
                    {dueDate ? formatDate(parseLocalDate(dueDate), currentUser?.dateFormat) : '-'}
                </span>
            )
        },
        { label: 'Closed Date', value: closedDate ? formatDate(parseLocalDate(closedDate), currentUser?.dateFormat) : '-' }
    ];

    return (
        <main className="bg-gray-50 p-2 md:p-4">
            <button
                className="mb-6 text-indigo-600 hover:underline w-fit"
                onClick={() => navigate('/opportunities')}
            >
                &larr; Back
            </button>

            {/* Page title with Edit button immediately to its right */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center">
                    <h1 className="text-2xl font-bold mr-3">Opportunity Details</h1>
                    {can(FEATURE_PERMISSIONS.OPP_EDIT) && (
                        <button
                            className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
                            onClick={() => setShowEditModal(true)}
                        >
                            Edit
                        </button>
                    )}
                </div>
                {quoting.canBuild && opportunity?.customerId && (
                    <button
                        type="button"
                        title="Create an editable quote draft for this opportunity"
                        onClick={() => navigate(`/quotes/new?customerId=${encodeURIComponent(opportunity.customerId)}&opportunityId=${encodeURIComponent(opportunity.id || id)}`)}
                        className="rounded bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700"
                    >
                        Build quote
                    </button>
                )}
            </div>

            {/* Guided qualification (AI Sales Agent Phase 4) — hidden unless the owner enables it */}
            <QualificationCard
                opportunity={opportunity}
                currentUser={user}
                canEdit={canModifyData(currentUser) && canEditStage}
                users={users}
                canAssignAny={can(FEATURE_PERMISSIONS.TASK_ASSIGN_ANY)}
                formatters={{
                    valueLabel: getValueRangeLabel,
                    formatDate: date => formatDate(date, currentUser?.dateFormat)
                }}
                onUpdated={(key, entry) => setOpportunity(prev => ({
                    ...prev,
                    qualification: { ...(prev.qualification || {}), [key]: entry }
                }))}
                onToast={setToast}
            />

            {/* Fields in two columns — right column starts with Start Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-3">
                    {/* Custom Stage row with inline edit for assigned non-admin users */}
                    <div className="flex items-center text-lg text-gray-600 font-semibold">
                      Stage:
                      <span className="ml-2 font-normal">
                        <span className={
                          `px-3 py-1 text-sm font-semibold rounded-full ${
                            stage === 'New' ? 'bg-teal-100 text-teal-700' :
                            stage === 'Qualified' ? 'bg-gray-100 text-purple-700' :                            
                            stage === 'Not Qualified' ? 'bg-gray-100 text-gray-600' :
                            stage === 'Proposal Sent' ? 'bg-gray-100 text-yellow-700' :
                            stage === 'Closed Won' ? 'bg-gray-100 text-green-700' :
                            stage === 'Closed Lost' ? 'bg-gray-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`
                        }>
                          {stage || '-'}
                        </span>
                      </span>
                    </div>

                    {leftFields.map(({ label, value }) => (
                        <div key={label} className="flex items-center text-lg text-gray-600 font-semibold">
                            {label}:
                            <span className="ml-2 text-gray-900 font-normal">
                                {label === 'Customer Name' && opportunity?.customerId ? (
                                    can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) || 
                                    (customers && customers.some(c => String(c.id) === String(opportunity.customerId))) ? (
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/customer/${opportunity.customerId}`)}
                                            className="text-indigo-600 hover:underline"
                                        >
                                            {value}
                                        </button>
                                    ) : (
                                        <span className="text-gray-900" title="You don't have access to view this customer's details">
                                            {value} 
                                        </span>
                                    )
                                ) : (
                                    value
                                )}
                            </span>
                        </div>
                    ))}
                </div>

                <div className="space-y-3">
                    {rightFields.map(({ label, value }) => (
                        <div key={label} className="flex items-center text-lg text-gray-600 font-semibold">
                            {label}:
                            <span className="ml-2 text-gray-900 font-normal">{value}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Re-Engagement Follow-Up — visible for Closed Lost and Not Qualified stages */}
            {(stage === 'Closed Lost' || stage === 'Not Qualified') && (
                <FollowUpCard
                    opportunity={opportunity}
                    oppId={id}
                    currentUser={user}
                    can={can}
                    onUpdate={(updates) => setOpportunity(prev => ({ ...prev, ...updates }))}
                />
            )}

            {/* Tabs for Notes, Attachments, and Activity Log */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('notes')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'notes'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Notes ({notesCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('tasks')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'tasks'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Tasks ({tasks.filter(t => t.status === 'open' || t.status === 'Open').length})
                    </button>
                    <button
                        onClick={() => setActiveTab('attachments')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'attachments'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Attachments ({attachments.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('activity')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'activity'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Activity Log
                    </button>
                </nav>
            </div>
            <div className="bg-white rounded-lg shadow">
                {activeTab === 'notes' && (
                    <div className="p-6">
                        {/* Stage Change Reason — shown when opportunity is Not Qualified / Closed Lost */}
                        {opportunity?.stageChangeReason && (stage === 'Not Qualified' || stage === 'Closed Lost') && (
                            <div className="mb-4 border-l-4 border-amber-400 bg-amber-50 rounded-r-lg p-4">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Stage Change Reason</p>
                                <p className="text-sm text-gray-800">{opportunity.stageChangeReason}</p>
                            </div>
                        )}
                        <UnifiedNotes 
                            entityType="opportunity" 
                            entityId={id} 
                            maxNotes={100}
                            onNotesLoaded={setNotesCount}
                        />
                    </div>
                )}
                
                {activeTab === 'tasks' && (
                    <div className="p-6">
                        <div className="mb-4 bg-blue-50 rounded-lg p-3">
                            <p className="text-sm text-gray-700">
                                <strong>Related Tasks:</strong> Tasks associated with this opportunity.
                            </p>
                        </div>
                        
                        {tasksLoading ? (
                            <div className="text-center py-8 text-gray-500">Loading tasks...</div>
                        ) : tasks.filter(t => t.status === 'open' || t.status === 'Open').length === 0 ? (
                            <div className="text-center py-8 text-gray-500">No open tasks for this opportunity.</div>
                        ) : (
                            <div className="space-y-3">
                                {tasks.filter(t => t.status === 'open' || t.status === 'Open').map(task => {
                                    const isPastDue = task.dueDate && new Date(task.dueDate.toDate ? task.dueDate.toDate() : task.dueDate) < new Date() && task.status !== 'completed';
                                    
                                    return (
                                        <div
                                            key={task.id}
                                            className={`bg-white rounded-lg p-4 border-l-4 ${
                                                isPastDue ? 'border-red-500' : 'border-yellow-500'
                                            } shadow-sm hover:shadow-md transition-shadow`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <Link to={`/tasks/${task.id}`} className="font-semibold text-indigo-600 hover:text-indigo-800 hover:underline">
                                                            {task.title}
                                                        </Link>
                                                        <span className={`text-xs px-2 py-1 rounded ${
                                                            isPastDue ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                            {isPastDue ? 'Overdue' : 'Pending'}
                                                        </span>
                                                    </div>
                                                    {task.description && (
                                                        <p className="text-sm text-gray-600 mt-2">{task.description}</p>
                                                    )}
                                                    <div className="mt-3 text-xs text-gray-500">
                                                        <span>Due: {task.dueDate ? formatDate(task.dueDate.toDate ? task.dueDate.toDate() : task.dueDate) : 'No date'}</span>
                                                        {task.assignedTo && <span className="ml-3">Assigned to: {task.assignedTo}</span>}
                                                    </div>
                                                    
                                                    {task.associations?.customerName && (
                                                        <div className="mt-3">
                                                            <a
                                                                href={`/customers/${task.associations.customerId}`}
                                                                className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded hover:bg-gray-200"
                                                            >
                                                                <span>🏢</span>
                                                                <span>{task.associations.customerName}</span>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
                
                {activeTab === 'attachments' && (
                    <div className="p-6">
                        <h3 className="text-lg font-semibold mb-4">Opportunity Attachments</h3>
                        {can(FEATURE_PERMISSIONS.OPP_EDIT) && (
                        <div className="mb-4">
                            <label className="px-4 py-2 bg-indigo-600 text-white rounded cursor-pointer hover:bg-indigo-500">
                                {uploading ? 'Uploading...' : 'Upload File'}
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.xlsx,.csv"
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                    disabled={uploading}
                                    ref={fileInputRef}
                                />
                            </label>
                            {uploadError && <span className="ml-4 text-red-600">{uploadError}</span>}
                        </div>
                        )}
                        <ul className="divide-y">
                            {attachments.length === 0 ? (
                                <li className="py-4 text-gray-500">No attachments uploaded.</li>
                            ) : (
                                attachments.map(att => {
                                    const uploader = users.find(u => u.uid === att.userId || u.email === att.userEmail);
                                    const uploaderName = uploader
                                        ? (uploader.firstName || uploader.lastName
                                            ? `${uploader.firstName || ''} ${uploader.lastName || ''}`.trim()
                                            : uploader.displayName || uploader.name || uploader.email || uploader.uid)
                                        : att.userEmail || att.userId || 'Unknown';
                                    const uploadedDate = att.uploadedAt && att.uploadedAt.toDate
                                        ? att.uploadedAt.toDate().toLocaleString()
                                        : '';

                                    return (
                                        <li key={att.id} className="py-2 flex items-center justify-between">
                                            <div>
                                                <a
                                                    href={att.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-indigo-600 hover:underline"
                                                >
                                                    {att.name}
                                                </a>
                                            </div>
                                            <div className="flex items-center space-x-4">
                                                <span className="text-xs text-gray-400">
                                                    Uploaded: <span className="font-semibold">{uploaderName}</span>
                                                    {uploadedDate && ` - ${uploadedDate}`}
                                                </span>
                                                {can(FEATURE_PERMISSIONS.OPP_EDIT) && (
                                                <button
                                                    className="ml-4 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-500 text-xs"
                                                    onClick={() => handleDeleteAttachment(att)}
                                                >
                                                    Delete
                                                </button>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })
                            )}
                        </ul>
                    </div>
                )}
                
                {activeTab === 'activity' && (
                    <div className="p-6">
                        <h3 className="text-lg font-semibold mb-4">Activity Log</h3>
                            <div className="overflow-x-auto">
                                <div className="flex items-center mb-3">
                                    <span className="mr-2 font-medium text-gray-700">Search:</span>
                                    <input
                                        type="text"
                                        className={`w-full border rounded px-3 py-2 transition-colors duration-150 ${
                                            logSearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                        }`}
                                        placeholder="Search activity log..."
                                        value={logSearch}
                                        onChange={e => setLogSearch(e.target.value)}
                                    />
                                </div>
                                <table className="w-full whitespace-no-wrap">
                                    <thead>
                                        <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                            <th className="px-4 py-3 whitespace-nowrap">Date</th>
                                            <th className="px-4 py-3 whitespace-nowrap">Time</th>
                                            <th className="px-4 py-3 whitespace-nowrap">User</th>
                                            <th className="px-4 py-3 whitespace-nowrap">Action</th>
                                            <th className="px-4 py-3">Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedLogs.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="text-center py-4 text-gray-500">No activity found.</td>
                                            </tr>
                                        ) : (
                                            paginatedLogs.map(log => {
                                                const dateObj = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
                                                return (
                                                    <tr key={log.id} className="text-gray-700 hover:bg-gray-50">
                                                        <td className="px-4 py-3 whitespace-nowrap">{formatDate(dateObj, currentUser?.dateFormat)}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap">{dateObj.toLocaleTimeString()}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            {log.userName || ((log.firstName || log.lastName)
                                                                ? `${log.firstName || ''} ${log.lastName || ''}`.trim()
                                                                : (log.displayName || log.userEmail || log.userId || '-'))}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap">{log.action || '-'}</td>
                                                        <td className="px-4 py-3">{log.details || '-'}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                                <PaginationControls
                                    currentPage={logPage}
                                    totalPages={totalLogPages}
                                    onPageChange={setLogPage}
                                />
                            </div>
                    </div>
                )}
            </div>

            {/* Toast Notification */}
            {toast && (
                <div className={`fixed bottom-4 right-4 px-6 py-3 rounded shadow-lg ${
                    toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
                } text-white`}>
                    {toast.message}
                </div>
            )}

            {/* OppModal as a popout */}
            {showEditModal && (
                <OppModal
                    user={user}
                    users={users}
                    customers={customers}
                    initial={opportunity}
                    onSaved={async (updatedOpp) => {
                        setShowEditModal(false);
                        // Cloud function may create tasks on stage change — refresh after a short delay
                        setTimeout(() => refreshTasks(), 3000);
                        if (updatedOpp && updatedOpp.id) {
                            setOpportunity(prev => ({ ...prev, ...updatedOpp }));

                            // Update Customer Name - use denormalized field first, then fallback to lookup
                            if (updatedOpp.customerName) {
                                setcustomerName(updatedOpp.customerName);
                            } else {
                                const found = customers.find(c =>
                                    String(c.id) === String(updatedOpp.customerId) ||
                                    String(c.customerId) === String(updatedOpp.customerId)
                                );
                                setcustomerName(found?.name || found?.customerName || '-');
                            }

                            // Update sales rep name immediately
                            if (updatedOpp.uid) {
                                const u = users.find(u => u.id === updatedOpp.uid || u.uid === updatedOpp.uid);
                                setSalesRepName(
                                    (u?.firstName && u?.lastName && `${u.firstName} ${u.lastName}`) ||
                                    u?.displayName || u?.email || u?.id || updatedOpp.uid || '-'
                                );
                            } else {
                                setSalesRepName('-');
                            }
                        } else {
                            // fallback: re-fetch
                            const docRef = doc(db, collectionPath('opportunities'), id);
                            const docSnap = await getDoc(docRef);
                            if (docSnap.exists()) {
                                const opp = { id: docSnap.id, ...docSnap.data() };
                                setOpportunity(opp);

                                // Update Customer Name - use denormalized field first
                                if (opp.customerName) {
                                    setcustomerName(opp.customerName);
                                } else {
                                    const found = customers.find(c =>
                                        String(c.id) === String(opp.customerId) ||
                                        String(c.customerId) === String(opp.customerId)
                                    );
                                    setcustomerName(found?.name || found?.customerName || '-');
                                }

                                // Update sales rep name
                                if (opp.uid) {
                                    const u = users.find(u => u.id === opp.uid || u.uid === opp.uid);
                                    setSalesRepName(
                                        (u?.firstName && u?.lastName && `${u.firstName} ${u.lastName}`) ||
                                        u?.displayName || u?.email || u?.id || opp.uid || '-'
                                    );
                                } else {
                                    setSalesRepName('-');
                                }
                            }
                        }
                    }}
                    onClose={() => setShowEditModal(false)}
                />
            )}

        </main>
    );
};

export default OppDetailPage;



